import { useState } from 'react';
import { Avatar, Box, Flex, Icon, Portal, Tooltip } from '@chakra-ui/react';
import { KText, KAvatarItem } from '@/ui-library/components';
import { Hireflix } from '@/assets/icons/ats';
import Cross from '@/assets/icons/search/Cross';
import { KPopover } from '@/ui-library/components/organisms/KPopover';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import extAssessmentErrors from '@/utils/errors/Modules/ext-assessment';
import { ChevronDown } from '@kula-ai/icons-core';

interface HireflixNotificationProps {
  skippedCandidates: {
    skipped_missing_email: any[];
    skipped_already_invited: any[];
    errors: any[];
  } | null;
  onClose: () => void;
}

export const HireflixNotification: React.FC<HireflixNotificationProps> = ({
  skippedCandidates,
  onClose,
}) => {
  const [minimized, setMinimized] = useState(false);

  if (!skippedCandidates) return null;

  const { skipped_missing_email, skipped_already_invited, errors } =
    skippedCandidates;
  const hasSkipped =
    skipped_missing_email?.length > 0 || skipped_already_invited?.length > 0;

  if (!hasSkipped) return null;

  const getErrorMessage = (error: any) => {
    const errorCode = error?.error || error?.error_code || error?.code;
    if (errorCode && extAssessmentErrors[errorCode]) {
      return extAssessmentErrors[errorCode].message;
    }
  };

  const renderCandidateTag = (candidate: any) => {
    const {
      first_name = '',
      last_name = '',
      image_url,
    } = candidate?.contact || {};
    const name = `${first_name} ${last_name}`.trim();

    return (
      <Flex
        bg="white"
        p="6px 8px"
        borderRadius="6px"
        alignItems="center"
        gap="6px"
      >
        <Avatar
          name={name}
          w="20px"
          h="20px"
          borderRadius="3px"
          src={image_url}
        />
        <TruncatedTextWithTooltip
          size="sm-medium"
          text={name}
          maxW="100px"
          enableTooltip
        />
      </Flex>
    );
  };

  const renderCandidateSection = (
    candidates: any[],
    message: string,
    bgColor: string,
    color: string
  ) => {
    if (!candidates?.length) return null;

    const VISIBLE_COUNT = 1;
    const visibleCandidates = candidates.slice(0, VISIBLE_COUNT);
    const hiddenCandidates = candidates.slice(VISIBLE_COUNT);
    const remainingCount = hiddenCandidates.length;

    const renderPlusMoreTrigger = () => (
      <Flex
        bg="white"
        w="33px"
        h="33px"
        alignItems="center"
        justifyContent="center"
        borderRadius="6px"
        border="1px solid"
        borderColor="white"
        cursor="pointer"
      >
        <KText color="secondary-700" fontWeight="550" fontSize="12px">
          +{remainingCount}
        </KText>
      </Flex>
    );

    const candidateCountText =
      candidates.length === 1
        ? '1 candidate'
        : `${candidates.length} candidates`;

    return (
      <Flex
        bg={bgColor}
        borderRadius="sm"
        p="5px 10px"
        flexDir="column"
        gap="8px"
      >
        <Flex gap="8px" alignItems="flex-start">
          <Flex flexDir="column" gap="8px" flex="1">
            <KText color={color} size="sm">
              Skipped <strong>{candidateCountText}</strong> as {message}
            </KText>
            <Flex gap="6px" flexWrap="wrap">
              {visibleCandidates.map((candidate: any, idx: number) => (
                <Box key={idx}>{renderCandidateTag(candidate)}</Box>
              ))}
              {remainingCount > 0 && (
                <KPopover
                  popoverProps={{ trigger: 'hover' }}
                  triggerElement={renderPlusMoreTrigger()}
                  showCloseButton={false}
                  popoverContentProps={{
                    p: '0',
                    maxHeight: '340px',
                    overflow: 'scroll',
                  }}
                  popoverBodyProps={{ p: '0px' }}
                >
                  <>
                    {hiddenCandidates.map((candidate: any, idx: number) => {
                      const {
                        first_name = '',
                        last_name = '',
                        image_url,
                      } = candidate?.contact || {};
                      const name = `${first_name} ${last_name}`.trim();

                      return (
                        <KAvatarItem
                          key={idx}
                          user={candidate}
                          name={name}
                          src={image_url || null}
                          showName
                          avatarProps={{
                            className: 'avatar-xs',
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            border:
                              '1px solid var(--kula-dark-secondary-100, #fff)',
                          }}
                          containerProps={{
                            p: '10px',
                            borderBottom:
                              '1px solid var(--kula-colors-secondary-100, #E9EBF4)',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                          size="sm"
                          lineHeight="1rem"
                        />
                      );
                    })}
                  </>
                </KPopover>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  return (
    <Portal>
      <Box
        pos="fixed"
        bottom="0"
        minHeight="100px"
        width="450px"
        border="1px solid var(--kula-dark-secondary-100, #EDEDED)"
        boxShadow="0px 183px 73px rgba(0, 0, 0, 0.01), 0px 103px 62px rgba(0, 0, 0, 0.05), 0px 46px 46px rgba(0, 0, 0, 0.09), 0px 11px 25px rgba(0, 0, 0, 0.1), 0px 0px 0px rgba(0, 0, 0, 0.1)"
        bg="white"
        left="100px"
        borderRadius="12px 12px 0 0"
        zIndex="1600"
        transition="transform 0.3s ease-in-out"
        transform={
          minimized ? 'translateY(calc(100% - 50px))' : 'translateY(0)'
        }
      >
        <Flex
          h="50px"
          bg="secondary-900"
          borderRadius="12px 12px 0 0"
          color="white"
          alignItems="center"
          px="24px"
        >
          <Flex alignItems="center" gap="8px" flex={1}>
            <Icon as={Hireflix} w="24px" h="24px" />
            <KText color="base-white" size="sm-medium">
              Sending Hireflix assessment
            </KText>
          </Flex>
          <Box mr="8px">
            <Tooltip
              label="Minimize"
              arrowSize={8}
              hasArrow
              placement="top"
              borderRadius="4px"
            >
              <Icon
                transform={minimized ? 'rotateX(180deg)' : 'none'}
                as={ChevronDown}
                width="24px"
                height="24px"
                color="white"
                cursor="pointer"
                onClick={() => setMinimized(!minimized)}
              />
            </Tooltip>
          </Box>
          <Box>
            <Tooltip
              label="Close"
              arrowSize={8}
              hasArrow
              placement="top"
              borderRadius="4px"
            >
              <div>
                <Icon
                  as={Cross}
                  width="20px"
                  height="20px"
                  color="white"
                  cursor="pointer"
                  onClick={onClose}
                />
              </div>
            </Tooltip>
          </Box>
        </Flex>
        {!minimized && (
          <Flex flexDir="column" gap="12px" p="14px">
            {renderCandidateSection(
              skipped_missing_email,
              "they don't have email addresses",
              'pastel-1-light',
              'pastel-1-dark'
            )}
            {renderCandidateSection(
              skipped_already_invited,
              'they have already received this assessment',
              'pastel-6-light',
              'pastel-6-dark'
            )}
            {errors?.length > 0 &&
              (() => {
                const errorsByMessage = errors.reduce(
                  (currentError: any, err: any) => {
                    const message = getErrorMessage(err);
                    if (!currentError[message]) {
                      currentError[message] = [];
                    }
                    currentError[message].push({
                      ...err,
                      contact: err.contact,
                    });
                    return currentError;
                  },
                  {}
                );
                return Object.entries(errorsByMessage).map(
                  ([message, errs]: [string, any]) => (
                    <Box key={message}>
                      {renderCandidateSection(
                        errs,
                        message,
                        'pastel-1-light',
                        'pastel-1-dark'
                      )}
                    </Box>
                  )
                );
              })()}
          </Flex>
        )}
      </Box>
    </Portal>
  );
};
